import React from "react";
import { Modal, Button, Typography, List, Space } from "antd";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import {
  groupActivityLogs,
  getActivityLogEventTitleByEvent,
  getActivityLogEventDescriptionByEventAndCount,
  getActivityLogEventIconByEvent,
} from "../../utils/interview-utils";
import { WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./modals.scss";

interface AiAssessmentFailureReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Violation = {
  title: string;
  description: string;
  icon: React.ReactNode;
  timestamp?: string;
  date?: string;
  time?: string;
  sortTimestamp?: dayjs.Dayjs;
};

const AiAssessmentFailureReasonModal: React.FC<
  AiAssessmentFailureReasonModalProps
> = ({ isOpen, onClose }) => {
  const { currentSession } = useAIAssessmentContext();

  if (!currentSession) return null;

  const { activityLogs, missedPings, lastPingAt } = currentSession;

  const groupedActivityLogs = groupActivityLogs(activityLogs || []);

  const currentViolations: Violation[] = groupedActivityLogs
    .filter((log) => log.event)
    .map((log) => {
      // Parse the formatted timestamp string back to dayjs for sorting
      const timestamp = log.lastOccurred
        ? dayjs(log.lastOccurred, "DD MMM YYYY hh:mm A", true)
        : null;
      const isValidTimestamp = timestamp && timestamp.isValid();
      const event = log.event || "unknown";
      return {
        title: getActivityLogEventTitleByEvent(event),
        description: getActivityLogEventDescriptionByEventAndCount(
          event,
          log.count || 1
        ),
        icon: getActivityLogEventIconByEvent(event),
        timestamp: log.lastOccurred,
        date: isValidTimestamp ? timestamp.format("DD MMM YYYY") : undefined,
        time: isValidTimestamp ? timestamp.format("hh:mm A") : undefined,
        sortTimestamp: isValidTimestamp ? timestamp : undefined,
      };
    });

  if (missedPings && missedPings > 0) {
    const pingTimestamp = dayjs(lastPingAt);
    currentViolations.push({
      title: "Connection Interruption",
      description:
        "Your internet connection was interrupted or unstable during the AI Assessment Round.",
      icon: <WarningOutlined />,
      timestamp: pingTimestamp.format("DD MMM YYYY hh:mm A"),
      date: pingTimestamp.format("DD MMM YYYY"),
      time: pingTimestamp.format("hh:mm A"),
      sortTimestamp: pingTimestamp,
    });
  }

  currentViolations.sort((a, b) => {
    if (!a.sortTimestamp && !b.sortTimestamp) return 0;
    if (!a.sortTimestamp) return 1;
    if (!b.sortTimestamp) return -1;
    return b.sortTimestamp.valueOf() - a.sortTimestamp.valueOf();
  });

  return (
    <Modal
      closeIcon={false}
      destroyOnClose={true}
      centered
      open={isOpen}
      onCancel={onClose}
      footer={null}
      className="ai-assessment-failure-reason-modal"
    >
      <div className="modal-content">
        <div className="modal-content-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            View Violations
          </Typography.Title>
        </div>

        {currentViolations.length > 0 && (
          <List size="small" style={{ width: "100%", padding: "16px 0px" }}>
            {currentViolations.map((violation, index) => (
              <List.Item
                key={`${violation.title}-${violation.timestamp || index}`}
              >
                <List.Item.Meta
                  avatar={violation.icon}
                  title={
                    <Typography.Text type="danger" style={{ fontSize: "14px" }}>
                      {violation.title}
                    </Typography.Text>
                  }
                  description={violation.description}
                />
                <Space direction="vertical" size="small">
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: "12px" }}
                  >
                    {violation.date}
                  </Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: "12px" }}
                  >
                    {violation.time}
                  </Typography.Text>
                </Space>
              </List.Item>
            ))}
          </List>
        )}
      </div>
      <div className="ai-assessment-modal-footer">
        <Button type="default" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default AiAssessmentFailureReasonModal;
